import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../db/firebase.js';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { useCronContext } from './CronContext.js';
import { logActivity } from './CronContext.js';

const CONFIG_DOC = doc(db, 'config', 'nftIds');
const ITEMS_PER_PAGE = 10;

const AdminNFTIds = () => {
    const { isRunning, triggerCron } = useCronContext();

    const [ids, setIds] = useState([]);
    const [nftData, setNftData] = useState({});
    const [addInput, setAddInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [runCronAfter, setRunCronAfter] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const flash = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [configSnap, nftsSnap] = await Promise.all([
                getDoc(CONFIG_DOC),
                getDocs(collection(db, 'nfts')),
            ]);
            const loadedIds = configSnap.exists() ? configSnap.data().ids || [] : [];
            setIds(loadedIds);

            const data = {};
            nftsSnap.docs.forEach(d => {
                const nft = d.data();
                if (nft.id) data[nft.id] = nft;
            });
            setNftData(data);
        } catch (err) {
            flash('Failed to load NFT IDs: ' + err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, ids.length]);

    const saveIds = async (newIds) => {
        setSaving(true);
        try {
            await setDoc(CONFIG_DOC, { ids: newIds });
            setIds(newIds);
            return true;
        } catch (err) {
            flash('Failed to save: ' + err.message, 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async () => {
        const incoming = addInput
            .split(/[\n,\s]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0);

        if (incoming.length === 0) { flash('Please enter at least one ID.', 'error'); return; }

        const invalid = incoming.filter(id => !/^\d+$/.test(id));
        if (invalid.length > 0) {
            flash(`Invalid IDs (must be numeric): ${invalid.join(', ')}`, 'error');
            return;
        }

        const existing = new Set(ids);
        const duplicates = incoming.filter(id => existing.has(id));
        const toAdd = incoming.filter(id => !existing.has(id));

        if (toAdd.length === 0) {
            flash(`All ${duplicates.length} ID(s) already exist.`, 'error');
            return;
        }

        const newIds = [...ids, ...toAdd];
        const ok = await saveIds(newIds);
        if (ok) {
            setAddInput('');
            await logActivity('ids_added', `Added ${toAdd.length} ID(s): ${toAdd.join(', ')}`);

            if (runCronAfter) {
                if (isRunning) {
                    flash(`Added ${toAdd.length} ID(s). Cron already running — new IDs will be picked up next cycle.`);
                } else {
                    const result = await triggerCron();
                    const dup = duplicates.length > 0 ? ` ${duplicates.length} duplicate(s) skipped.` : '';
                    flash(`Added ${toAdd.length} ID(s).${dup} ${result.message}`, result.ok ? 'success' : 'error');
                }
            } else {
                const dup = duplicates.length > 0 ? ` ${duplicates.length} duplicate(s) skipped.` : '';
                flash(`Added ${toAdd.length} ID(s).${dup}`);
            }
        }
    };

    const handleRemove = async (id) => {
        const newIds = ids.filter(i => i !== id);
        const ok = await saveIds(newIds);
        if (ok) {
            const name = nftData[id]?.name || id;
            await logActivity('id_removed', `Removed ID ${id} (${name})`);
            flash(`Removed ID ${id}.`);
            setConfirmRemove(null);
            if (expandedId === id) setExpandedId(null);
        }
    };

    const filteredIds = searchTerm
        ? ids.filter(id => {
            const name = nftData[id]?.name?.toLowerCase() || '';
            return id.includes(searchTerm) || name.includes(searchTerm.toLowerCase());
        })
        : ids;

    const reversedIds = [...filteredIds].reverse();
    const totalPages = Math.ceil(reversedIds.length / ITEMS_PER_PAGE) || 1;
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const pageIds = reversedIds.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    if (loading) return <div className="admin-loading">Loading NFT IDs...</div>;

    return (
        <div className="admin-section">
            <h2 className="admin-section-title">NFT IDs</h2>

            {message && (
                <div className={`admin-message admin-message-${message.type}`}>{message.text}</div>
            )}

            <div className="admin-add-panel">
                <h3>Add IDs</h3>
                <p className="admin-hint">Paste one or more token IDs (numeric), separated by newlines, spaces, or commas.</p>
                <textarea
                    className="admin-textarea"
                    rows={4}
                    placeholder={"123456\n789012\n345678"}
                    value={addInput}
                    onChange={e => setAddInput(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                        className="admin-btn admin-btn-primary"
                        onClick={handleAdd}
                        disabled={saving || !addInput.trim()}
                    >
                        {saving ? 'Saving...' : 'Add IDs'}
                    </button>
                    <label className="admin-checkbox-row">
                        <input
                            type="checkbox"
                            checked={runCronAfter}
                            onChange={e => setRunCronAfter(e.target.checked)}
                        />
                        Trigger cron after saving
                        {isRunning && <span className="admin-muted"> (cron already running)</span>}
                    </label>
                </div>
            </div>

            <div className="admin-nft-list-header">
                <span className="admin-nft-count">{ids.length} IDs tracked</span>
                <input
                    type="text"
                    className="admin-search"
                    placeholder="Search by ID or name..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="admin-table-wrapper">
                <table className="admin-table admin-table-nft-ids">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Token ID</th>
                            <th>Mythic Name</th>
                            <th>Rank</th>
                            <th>In Firestore</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageIds.map((id, index) => {
                            const nft = nftData[id];
                            const isExpanded = expandedId === id;
                            return (
                                <React.Fragment key={id}>
                                    <tr
                                        style={{ cursor: nft ? 'pointer' : 'default' }}
                                        onClick={() => nft && setExpandedId(isExpanded ? null : id)}
                                    >
                                        <td className="admin-td-num">{ids.indexOf(id) + 1}</td>
                                        <td><code>{id}</code></td>
                                        <td>{nft?.name || <span className="admin-muted">Not fetched yet</span>}</td>
                                        <td>{nft?.rank ?? <span className="admin-muted">—</span>}</td>
                                        <td>
                                            <span className={`admin-badge ${nft ? 'admin-badge-green' : 'admin-badge-gray'}`}>
                                                {nft ? 'Yes' : 'Pending cron'}
                                            </span>
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            {confirmRemove === id ? (
                                                <span className="admin-confirm-row">
                                                    <button
                                                        className="admin-btn admin-btn-danger admin-btn-sm"
                                                        onClick={() => handleRemove(id)}
                                                        disabled={saving}
                                                    >
                                                        Confirm
                                                    </button>
                                                    <button
                                                        className="admin-btn admin-btn-ghost admin-btn-sm"
                                                        onClick={() => setConfirmRemove(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </span>
                                            ) : (
                                                <button
                                                    className="admin-btn admin-btn-danger admin-btn-sm"
                                                    onClick={() => setConfirmRemove(id)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {isExpanded && nft && (
                                        <tr className="admin-row-expanded">
                                            <td colSpan={6}>
                                                <div className="admin-row-detail">
                                                    {nft.image_url && (
                                                        <img
                                                            src={nft.image_url}
                                                            alt={nft.name}
                                                            className="admin-row-detail-img"
                                                        />
                                                    )}
                                                    <div className="admin-row-detail-info">
                                                        {nft.description && <div>{nft.description}</div>}
                                                        {nft.mythicTrait && <div><strong>Mythic trait:</strong> {nft.mythicTrait}</div>}
                                                        {nft.birthday && <div><strong>Birthday:</strong> {nft.birthday}</div>}
                                                        {nft.owner && <div><strong>Owner:</strong> <code style={{ fontSize: '0.85em' }}>{nft.owner}</code></div>}
                                                        {nft.opensea_url && (
                                                            <a
                                                                href={nft.opensea_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="admin-link"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                View on OpenSea ↗
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {reversedIds.length === 0 && (
                            <tr>
                                <td colSpan={6} className="admin-empty">
                                    {searchTerm ? 'No IDs match your search.' : 'No IDs tracked yet.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {reversedIds.length > 0 && (
                <div className="admin-pagination">
                    <button
                        className="admin-btn admin-btn-ghost"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                    >
                        Previous
                    </button>
                    <span className="admin-pagination-info">
                        Page {currentPage} of {totalPages}
                        {reversedIds.length > 0 && ` (${startIdx + 1}-${Math.min(startIdx + ITEMS_PER_PAGE, reversedIds.length)} of ${reversedIds.length})`}
                    </span>
                    <button
                        className="admin-btn admin-btn-ghost"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminNFTIds;
